import { useEffect, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useT } from '@/i18n/LocaleProvider';
import { WordTruncate } from './WordTruncate';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  FolderPlus,
  MoreHorizontal,
  Plus,
  RotateCw,
  Settings,
  User,
  Users,
} from 'lucide-react';
import { actionFailureTextIn } from '@/services/actionFailure';
import {
  TOUCH_ONLY_MEDIA_QUERY,
  useMediaQuery,
  useTouchDesktopLayout,
} from '@/hooks/useMediaQuery';
import { SidebarActionsMenu, type ISidebarMenuEntry } from './SidebarActionsMenu';
import { RefusalDescription, useRefusalNote } from './RefusalNote';
import { gateControlProps } from '@/utils/gateControl';
import { accessReadGate, type WorkAreaGateType } from '@/utils/workAreaPermissionGate';
import { emptyNamespaceLineIndent, namespaceHeadingIndent } from './workAreaIndent';
import type { IWorkAreaNamespaceGroup } from './workAreaNamespaceGroups';
import { INLINE_EDIT_ATTRIBUTE } from '@/hooks/useDialogFocusTrap';
import { StagedPlaceholder } from '@/components/StagedPlaceholder';

/**
 * The pieces of the sidebar's namespace tree (I-0655, AC-M12): a namespace's heading, and the inline
 * control that creates an organisation or a subgroup. The tree itself is drawn by `ProjectSelector`
 * from `groupWorkAreasByNamespace`, so the namespace is the navigation rather than a panel beside it.
 */

const ICONS = {
  personal: User,
  organisation: Building2,
  subgroup: Building2,
  other: Users,
} as const;

/** Nothing here says a reader may not act — the default when a caller passes no `gate` at all. */
const ALWAYS_PERMITTED: WorkAreaGateType = { state: 'permitted', basis: 'granted' };

/** F-0584 MWI-16: where Invite people opens — the organisation's (or subgroup's) Invitations. */
export function namespaceInvitationsPath(namespaceId: string | null): string {
  return `/-/organisations/${namespaceId}/settings?section=invitations`;
}

type NamespaceInviteGateModule = typeof import('./namespaceInviteGate');
let inviteGateModule: NamespaceInviteGateModule | null = null;

/**
 * F-0584 MWI-16: Invite people's gate on the iPad's ⋯ (`namespaceInviteGate`), whose module is
 * loaded on first use so the seeded roles stay out of the launch entry (gate 707, T-0559). Its load
 * is an access read under KD-17 (`accessReadGate`): pending while it is in flight, never permitted;
 * a load that fails is refused, "Couldn't check your access", and pressing the entry loads it again.
 */
function useNamespaceInviteGate(
  rolesHere: readonly string[] | undefined,
  wanted: boolean,
): WorkAreaGateType {
  const [gateModule, setGateModule] = useState(inviteGateModule);
  // KD-17a: after a failed load, a retry is pending again, checking again, not still failed.
  const [load, setLoad] = useState({ attempt: 0, failed: false, retrying: false });
  useEffect(() => {
    if (!wanted || gateModule) return undefined;
    let live = true;
    import('./namespaceInviteGate').then(
      (loaded) => {
        inviteGateModule = loaded;
        if (live) setGateModule(loaded);
      },
      () => {
        if (live) setLoad((current) => ({ ...current, failed: true, retrying: false }));
      },
    );
    return () => {
      live = false;
    };
  }, [wanted, gateModule, load.attempt]);
  if (gateModule) return gateModule.namespaceInviteGate(rolesHere);
  return accessReadGate({
    settled: load.failed,
    retrying: load.retrying,
    retry: () =>
      setLoad((current) => ({ attempt: current.attempt + 1, failed: true, retrying: true })),
  });
}

interface NamespaceGroupHeadingProps {
  group: IWorkAreaNamespaceGroup;
  /** A control drawn over the namespace's icon while it shows: the reorder handle (F-0380). */
  leading?: ReactNode;
  /** The row's last controls, right-aligned after its actions: the edit mode's move arrows
   *  (I-1263, AC-18.11), drawn only where the row is 44 px tall (`actionsVisible`). */
  trailing?: ReactNode;
  /** Starts the create dialog with this namespace chosen. Absent where nobody may create here. */
  onCreateWorkArea?: () => void;
  /** Opens the inline "new subgroup" row under this group. Groups only. */
  onCreateSubgroup?: () => void;
  /**
   * F-0416: the actions show without hover or focus — the phone picker's edit mode, where there is
   * no hover and the person entered the mode to use them.
   */
  actionsVisible?: boolean;
  /**
   * Whether creating here is refused (SB-3, I-0676's three-state rule): governs both
   * `onCreateSubgroup` and `onCreateWorkArea`, the same single `gate` `OrganisationWorkAreasSettings`
   * (§10.8) keeps for its own New subgroup/New work area buttons. Presence of the callback above is
   * structural — whether this *kind* of namespace supports the action at all; this gate is the
   * permission answer on top of that. Missing permission never removes the button (`gateControlProps`
   * disables it, with its reason) — only a structurally impossible action (no `onCreateWorkArea`/
   * `onCreateSubgroup` passed at all) does. Omitted defaults to permitted, so every existing caller
   * that does not yet pass a gate keeps today's behaviour.
   */
  gate?: WorkAreaGateType;
  /**
   * SB-2: on a touch device, reveals this heading's action icons even without hover/focus — the
   * row that is selected (`projectStore.selectedNamespaceId`) or whose touch controls were tapped
   * open (`NamespaceReorder.tsx`'s `touchHandleVisible`). The caller computes the OR so this
   * component only needs one boolean. Ignored on a pointer/hover device, where hover and keyboard
   * focus already reveal the icons (built, see §10.11 "measured").
   */
  touchRevealed?: boolean;
  /**
   * Scopes the app to this namespace's whole subtree (F-0383, §10.9). Absent for a group with no
   * resolvable `namespaceId` (the "other work areas" fallback, headed by what a work area says
   * about a namespace the tree does not list) — there is nothing yet to scope to.
   */
  onSelectNamespace?: () => void;
  /**
   * U-0024 (IP-27): the namespace's move up and down, as the iPad sidebar's ⋯ offers them. `null`
   * at an edge; absent where the namespace does not reorder (the person's own).
   */
  moves?: { up: (() => void) | null; down: (() => void) | null; disabled: boolean };
}

/**
 * One namespace, as the heading its work areas sit under: its name, and its handle where the name
 * does not already say it. The actions show on hover **and on keyboard focus**, so they are never
 * reachable by pointer only — and, on a touch device with neither, only on the row `touchRevealed`
 * names (SB-2).
 */
export function NamespaceGroupHeading({
  group,
  leading,
  trailing,
  onCreateWorkArea,
  onCreateSubgroup,
  actionsVisible = false,
  gate = ALWAYS_PERMITTED,
  touchRevealed = false,
  onSelectNamespace,
  moves,
}: NamespaceGroupHeadingProps) {
  const t = useT();
  const Icon = ICONS[group.kind];
  const label = group.own ? t('navigation.namespaces.you', { name: group.name }) : group.name;
  const selectable = Boolean(onSelectNamespace && group.namespaceId);
  /*
    A subgroup sits under its organisation's heading already, so its own handle is the part worth
    the room: `platform`, not `acme/platform` truncated to `acm…`. The full path stays in the
    tooltip.
  */
  const handle = group.depth > 0 ? (group.fullPath.split('/').pop() ?? '') : group.fullPath;
  // `Acme acme` says the same thing twice; the handle shows where it adds something.
  const showHandle = handle !== '' && handle !== group.name.toLowerCase();
  const touchOnly = useMediaQuery(TOUCH_ONLY_MEDIA_QUERY);
  // U-0024 (IP-27): the iPad's sidebar — one 44 px line, the name and one ⋯ (`touchMenuEntries`).
  const touchMenu = useTouchDesktopLayout() && !actionsVisible;
  const inviteGate = useNamespaceInviteGate(group.rolesHere, touchMenu);
  const navigate = useNavigate();
  // SB-2: a coarse pointer with no hover never gets the hover/focus-within reveal below (there is
  // neither), so it is gated on `touchRevealed` instead — never on every row at once, the mutation
  // this guards against (`WorkAreaNamespaceTree.test.tsx`). F-0416: the phone picker's edit mode
  // shows them outright (`actionsVisible`).
  const reveal =
    actionsVisible || (touchOnly && touchRevealed)
      ? 'opacity-100'
      : touchOnly
        ? 'opacity-0'
        : 'opacity-0 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100';
  const actionBase = `grid h-6 w-6 shrink-0 place-items-center rounded text-gray-400 outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-500 ${reveal}`;
  const action = `${actionBase} hover:bg-[#DDE0E2] hover:text-gray-900 dark:hover:bg-[#303030] dark:hover:text-gray-100`;
  // I-1088: a refused create control is dimmed (`[data-gate='refused']`) and does not answer the
  // pointer the way one that acts does — no hover fill, no darker ink — or under the pointer, the
  // one moment a person looks at it, it read as enabled.
  const createAction = gate.state === 'refused' ? actionBase : action;
  // I-1678 (SB-9): a refused create's reason by `aria-describedby` too.
  const refusal = useRefusalNote(gate);

  const actionIcon = actionsVisible ? 'h-4 w-4' : 'h-3 w-3';

  // I-1677 (IP-33): on the iPad's 44 px line the name may take two lines; the desktop keeps one
  // (I-1345).
  const nameControlOf = (lines: 1 | 2) =>
    selectable ? (
      // F-0383, §10.9: the name is the click/Enter target for the namespace scope — the drag
      // handle (this heading's own `leading` slot) and the Settings/create buttons (further
      // right in this row) stay separate targets, never swallowed by this one.
      <button
        type="button"
        onClick={onSelectNamespace}
        title={group.fullPath || group.name}
        className="min-w-0 truncate rounded text-left outline-none hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-focus dark:hover:text-white"
      >
        {/* I-1345: cut at a word, never inside one. */}
        <WordTruncate
          text={label}
          title={group.fullPath || group.name}
          lines={lines}
          className={lines === 2 ? 'leading-4' : ''}
        />
      </button>
    ) : (
      <WordTruncate
        text={label}
        title={group.fullPath || group.name}
        lines={lines}
        className={lines === 2 ? 'leading-4' : ''}
      />
    );
  const nameControl = nameControlOf(1);
  /*
    I-1680 (SB-8): the handle is drawn whole or not at all, and the name keeps the line. It shrank
    with the name (`min-w-0 truncate`), so a long name beside it left a one-character sliver of it
    at 1280. Now it never shrinks: the line wraps, and a handle that does not fit beside the name
    goes to a second line the 28 px box clips. A name longer than the line has the line to itself
    and is cut at a word (`WordTruncate`). The whole path stays in the name's tooltip.

    On a touch surface (`data-touch-targets`: the drawer at 768) the name's button is 44 px tall,
    so the line is too, overhanging the 28 px row equally above and below as the button did before
    the line held it. In a 28 px line the button's first line overflowed downward from the top, and
    the name was drawn 8 px under its icon (gate 679's drawer screenshot). The second line still
    starts below the box, so a wrapped handle stays out of view.
  */
  const nameLine = (
    <span
      data-namespace-name-line=""
      className="flex h-7 min-w-0 flex-1 flex-wrap content-start items-center gap-x-1.5 overflow-hidden [&>*]:leading-7 [[data-touch-targets]_&]:my-[calc((theme(spacing.7)-theme(spacing.touch))/2)] [[data-touch-targets]_&]:h-touch"
    >
      {nameControl}
      {showHandle ? (
        <span
          data-namespace-handle=""
          className="shrink-0 whitespace-nowrap font-mono text-[11px] font-normal text-gray-400 dark:text-gray-500"
        >
          {handle}
        </span>
      ) : null}
    </span>
  );

  if (touchMenu) {
    const where = group.fullPath || group.name;
    const entries: ISidebarMenuEntry[] = [
      ...(onCreateWorkArea
        ? [
            {
              label: t('navigation.namespaces.newWorkAreaHere'),
              title: t('navigation.namespaces.newWorkAreaIn', { where }),
              onSelect: onCreateWorkArea,
              gate,
            },
          ]
        : []),
      ...(onCreateSubgroup
        ? [
            {
              label: t('navigation.namespaces.newSubgroup'),
              title: t('navigation.namespaces.newSubgroupIn', { where }),
              onSelect: onCreateSubgroup,
              gate,
            },
          ]
        : []),
      ...(group.kind === 'organisation' || group.kind === 'subgroup'
        ? [
            {
              label: t('navigation.namespaces.settings'),
              title: t('navigation.namespaces.settingsFor', { where: group.fullPath }),
              onSelect: () => navigate(`/-/organisations/${group.namespaceId}/settings`),
            },
            {
              label: t('navigation.namespaces.invitePeople'),
              title: t('navigation.namespaces.invitePeopleTo', { where: group.fullPath }),
              onSelect: () => navigate(namespaceInvitationsPath(group.namespaceId)),
              gate: inviteGate,
            },
          ]
        : []),
      ...(moves
        ? [
            {
              label: t('navigation.namespaces.moveUp'),
              title: t('navigation.namespaces.moveNamedUp', { name: group.name }),
              onSelect: () => moves.up?.(),
              disabled: moves.disabled || !moves.up,
            },
            {
              label: t('navigation.namespaces.moveDown'),
              title: t('navigation.namespaces.moveNamedDown', { name: group.name }),
              onSelect: () => moves.down?.(),
              disabled: moves.disabled || !moves.down,
            },
          ]
        : []),
    ];
    return (
      <div
        className="group mt-1 flex min-h-touch w-full items-center gap-1.5 rounded-md text-[12px] font-semibold text-gray-500 first:mt-0 dark:text-gray-400"
        style={{ paddingLeft: namespaceHeadingIndent(group.depth) }}
        data-testid="namespace-heading"
        data-namespace-heading-menu=""
      >
        <span className="namespace-leading relative grid h-3.5 w-3.5 shrink-0 place-items-center">
          <Icon
            className="namespace-leading-icon h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
            aria-hidden="true"
          />
          {leading}
        </span>
        {/*
          The name takes the line, and the handle is not drawn: on the simulator at 1180 the
          organisation's name was cut to "Shell Check…" beside its whole handle (AC-19.15: the
          namespace gives way before the name). `WordTruncate` cuts to the box it is given, so the
          name's box takes all the line leaves; the handle stays in the name's tooltip and in the
          ⋯'s label ("More for <path>").
        */}
        <span className="grid min-w-0 flex-1">{nameControlOf(2)}</span>
        <SidebarActionsMenu
          label={t('navigation.namespaces.moreFor', { where })}
          menuLabel={where}
          icon={<MoreHorizontal aria-hidden="true" className="h-4 w-4" />}
          entries={entries}
        />
      </div>
    );
  }

  return (
    <div
      className={`group mt-1 flex w-full items-center gap-1.5 rounded-md pr-1 font-semibold text-gray-500 first:mt-0 dark:text-gray-400 ${
        actionsVisible ? 'min-h-touch text-[13px]' : 'h-7 text-[12px]'
      }`}
      style={{ paddingLeft: namespaceHeadingIndent(group.depth) }}
      data-testid="namespace-heading"
    >
      {/*
        F-0380: the reorder handle takes the icon's place while it shows, and no room of its own.
        In the row's flow it pushed every organisation 20 px right of the person's own namespace,
        handle shown or not (gate 438's sidebar screenshots).
      */}
      <span className="namespace-leading relative grid h-3.5 w-3.5 shrink-0 place-items-center">
        <Icon
          className="namespace-leading-icon h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
          aria-hidden="true"
        />
        {leading}
      </span>
      {nameLine}
      {group.kind === 'organisation' || group.kind === 'subgroup' ? (
        // F-0347: a Settings button replaces the old People and access shortcut — it opens the
        // organisation's Settings, with People and access as one section among several.
        <Link
          to={`/-/organisations/${group.namespaceId}/settings`}
          aria-label={t('navigation.namespaces.settingsFor', { where: group.fullPath })}
          title={t('navigation.namespaces.settings')}
          className={action}
        >
          <Settings className={actionIcon} />
        </Link>
      ) : null}
      {onCreateSubgroup ? (
        <button
          type="button"
          aria-label={t('navigation.namespaces.newSubgroupIn', { where: group.fullPath })}
          className={createAction}
          onClick={onCreateSubgroup}
          {...gateControlProps(gate, { title: t('navigation.namespaces.newSubgroup') })}
          {...refusal.controlProps}
        >
          <FolderPlus className={actionIcon} />
        </button>
      ) : null}
      {onCreateWorkArea ? (
        <button
          type="button"
          aria-label={t('navigation.namespaces.newWorkAreaIn', {
            where: group.fullPath || group.name,
          })}
          className={createAction}
          onClick={onCreateWorkArea}
          {...gateControlProps(gate, { title: t('navigation.namespaces.newWorkAreaHere') })}
          {...refusal.controlProps}
        >
          <Plus className={actionIcon} />
        </button>
      ) : null}
      {trailing}
      {onCreateSubgroup || onCreateWorkArea ? (
        <RefusalDescription id={refusal.id} reason={refusal.reason} />
      ) : null}
    </div>
  );
}

/** The muted line under a namespace with nothing in it yet, pointing at the + beside its name. */
export function EmptyNamespaceLine({ depth }: { depth: number }) {
  const t = useT();
  return (
    <p
      className="py-0.5 text-[12px] leading-5 text-gray-400 dark:text-gray-500"
      style={{ paddingLeft: emptyNamespaceLineIndent(depth) }}
    >
      {t('navigation.selector.noneYet')}
    </p>
  );
}

/**
 * What the tree says while it cannot draw every namespace yet. Until `namespaceTree` answers, the
 * work areas are grouped by what each of them says about its namespace, so organisations with
 * nothing in them yet are missing: the reader is told so, rather than shown a tree that looks
 * complete and is not.
 */
export function NamespaceTreeStatus({
  loading,
  failed,
  onRetry,
}: {
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  const t = useT();
  if (failed) {
    return (
      <div
        role="alert"
        className="mx-0.5 my-1 flex items-center gap-2 rounded-md bg-red-50 py-1 pl-2 pr-1 text-[12px] leading-4 text-red-700 dark:bg-red-950/40 dark:text-red-200"
      >
        <span className="min-w-0 flex-1">{t('navigation.namespaces.loadFailed')}</span>
        <button
          type="button"
          aria-label={t('navigation.namespaces.retryLoading')}
          title={t('navigation.sidebar.retry')}
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-red-500 outline-none hover:bg-red-100 focus-visible:ring-2 focus-visible:ring-red-500/40 dark:text-red-300 dark:hover:bg-red-900/50"
          onClick={onRetry}
        >
          <RotateCw className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  if (!loading) return null;
  return (
    <StagedPlaceholder
      deadline={false}
      onRetry={onRetry}
      label={t('navigation.namespaces.loading')}
    >
      <div
        role="status"
        aria-label={t('navigation.namespaces.loading')}
        className="space-y-1.5 px-1.5 py-1.5"
      >
        <div className="h-3 w-24 evx-skeleton rounded" />
        <div className="ml-5 h-3 w-32 evx-skeleton rounded" />
      </div>
    </StagedPlaceholder>
  );
}

interface CreateNamespaceRowProps {
  depth: number;
  placeholder: string;
  label: string;
  onCancel: () => void;
  onSubmit: (path: string) => Promise<void>;
}

/** An inline create control — Enter to submit, Escape to cancel — rather than a modal for one field. */
export function CreateNamespaceRow({
  depth,
  placeholder,
  label,
  onCancel,
  onSubmit,
}: CreateNamespaceRowProps) {
  const t = useT();
  const [path, setPath] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    const trimmed = path.trim().toLowerCase();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError(actionFailureTextIn(t, err, t('navigation.actions.createIt')));
    } finally {
      setSubmitting(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') onCancel();
  }

  return (
    <form onSubmit={submit} style={{ paddingLeft: 4 + depth * 14 }} className="px-0.5 py-0.5">
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={path}
          onChange={(event) => setPath(event.target.value)}
          onKeyDown={onKeyDown}
          {...{ [INLINE_EDIT_ATTRIBUTE]: '' }}
          placeholder={placeholder}
          aria-label={label}
          disabled={submitting}
          className="h-7 min-w-0 flex-1 rounded-md border-0 bg-[#F0F1F2] px-2 text-[13px] text-gray-900 outline-none transition-colors focus:bg-white focus:ring-2 focus:ring-focus dark:bg-[#242424] dark:text-white dark:focus:bg-[#212121]"
        />
        <button
          type="button"
          aria-label={t('navigation.namespaces.cancel')}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-gray-400 hover:bg-[#E4E6E8] dark:hover:bg-[#1f1f1f]"
          onClick={onCancel}
        >
          ×
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-[11px] leading-4 text-red-600 dark:text-red-300">
          {error}
        </p>
      )}
    </form>
  );
}
